import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Edit3,
  Eye,
  KeyRound,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  activatePlatformTenantLlmRuntimeDraft,
  createPlatformTenantLlmRuntimeDraft,
  deletePlatformTenantLlmRuntimeDraft,
  deletePlatformTenantLlmRuntimeProfile,
  getBackendErrorMessage,
  getPlatformTenantLlmRuntimeActive,
  getPlatformTenantLlmRuntimeOptions,
  getPlatformTenantLlmRuntimeProfiles,
  patchPlatformTenantLlmRuntimeDraft,
  replacePlatformTenantLlmRuntimeCredential,
} from "../../apiPlatformTenants";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import {
  buildSaasCredentialPayload,
  buildSaasDraftPayload,
  createEmptySaasCredential,
  createEmptySaasRuntimeDraft,
  getDefaultSaasProvider,
  getSaasProfileStatus,
  getSaasProviderLabel,
  normalizeSaasProviderOptions,
  profileToSaasCredential,
  profileToSaasDraft,
  validateSaasCredential,
  validateSaasRuntimeDraft,
} from "./platformLlmRuntimeState";

function getProfileModelsLabel(profile) {
  const models = [profile?.primary_model, profile?.fast_model].filter(Boolean);
  return models.length > 0 ? models.join(" / ") : "Provider defaults";
}

function getProfileCredentialLabel(profile) {
  return profile?.credential?.display_name || profile?.credential_display_name || "Stored credential";
}

function getProfileVersionLabel(profile) {
  return profile?.version ? `v${profile.version}` : "Draft";
}

function getProviderDefaults(providerOptions, provider) {
  return providerOptions.find((option) => option.provider === provider) || null;
}

function RuntimeProfileBadge({ status }) {
  return (
    <span className={`platform-badge is-${status === "active" ? "active" : status === "draft" ? "demo" : "payg"}`}>
      {status === "active" ? "Active" : status === "draft" ? "Draft" : "Inactive"}
    </span>
  );
}

function ProviderModelPreview({ providerOptions, provider }) {
  const defaults = getProviderDefaults(providerOptions, provider);
  const models = [
    defaults?.primary_model || defaults?.default_primary_model,
    defaults?.fast_model || defaults?.default_fast_model,
  ].filter(Boolean);

  return (
    <div className="platform-preview-card platform-llm-model-preview">
      <span>Default models</span>
      <strong>{models.length > 0 ? models.join(" / ") : "Backend catalog defaults"}</strong>
    </div>
  );
}

function RuntimeFormFields({
  draft,
  credential,
  providerOptions,
  errors,
  onDraftChange,
  onCredentialChange,
  apiKeyLabel = "API key",
  apiKeyPlaceholder = "Enter provider API key",
  requireApiKey = true,
  disableProvider = false,
  readOnlyRuntime = false,
}) {
  return (
    <>
      <div className="platform-form-grid">
        <label className="platform-field platform-field--wide">
          <span>LLM provider <span className="platform-required">*</span></span>
          <select
            className={`platform-select ${errors.provider ? "is-error" : ""}`}
            value={draft.provider}
            onChange={(event) => onDraftChange("provider", event.target.value)}
            disabled={disableProvider || readOnlyRuntime}
          >
            {providerOptions.map((option) => (
              <option value={option.provider} key={option.provider}>
                {option.label || getSaasProviderLabel(option.provider)}
              </option>
            ))}
          </select>
          {errors.provider ? <small className="platform-field-error">{errors.provider}</small> : null}
        </label>

        <ProviderModelPreview providerOptions={providerOptions} provider={draft.provider} />

        <label className="platform-field">
          <span>Request timeout seconds</span>
          <Input
            value={draft.requestTimeoutSeconds}
            onChange={(event) => onDraftChange("requestTimeoutSeconds", event.target.value)}
            inputMode="numeric"
            placeholder="60"
            disabled={readOnlyRuntime}
            error={Boolean(errors.requestTimeoutSeconds)}
          />
          {errors.requestTimeoutSeconds ? (
            <small className="platform-field-error">{errors.requestTimeoutSeconds}</small>
          ) : null}
        </label>

        <label className="platform-field">
          <span>Max retries</span>
          <Input
            value={draft.maxRetries}
            onChange={(event) => onDraftChange("maxRetries", event.target.value)}
            inputMode="numeric"
            placeholder="2"
            disabled={readOnlyRuntime}
            error={Boolean(errors.maxRetries)}
          />
          {errors.maxRetries ? <small className="platform-field-error">{errors.maxRetries}</small> : null}
        </label>
      </div>

      <div className="platform-card-divider" />

      <div className="platform-form-grid">
        <label className="platform-field">
          <span>Credential display name</span>
          <Input
            value={credential.displayName}
            onChange={(event) => onCredentialChange("displayName", event.target.value)}
            placeholder={`${getSaasProviderLabel(draft.provider)} production key`}
          />
        </label>

        <label className="platform-field">
          <span>
            {apiKeyLabel}
            {requireApiKey ? <span className="platform-required"> *</span> : null}
          </span>
          <Input
            type="password"
            value={credential.apiKey}
            onChange={(event) => onCredentialChange("apiKey", event.target.value)}
            placeholder={apiKeyPlaceholder}
            error={Boolean(errors.apiKey)}
          />
          {errors.apiKey ? <small className="platform-field-error">{errors.apiKey}</small> : null}
        </label>
      </div>
    </>
  );
}

function RuntimeDialog({ title, children, onClose }) {
  return (
    <div className="platform-dialog-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="platform-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="platform-dialog-header">
          <h3 className="platform-card-title">{title}</h3>
          <button type="button" className="platform-icon-button" onClick={onClose} aria-label={`Close ${title}`}>
            <X size={16} />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

export default function PlatformTenantLlmRuntimePanel({ tenantId, disabled = false }) {
  const [providerOptions, setProviderOptions] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [draft, setDraft] = useState(createEmptySaasRuntimeDraft());
  const [credential, setCredential] = useState(createEmptySaasCredential());
  const [fieldErrors, setFieldErrors] = useState({});
  const [panelError, setPanelError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [actionInFlight, setActionInFlight] = useState("");
  const [editState, setEditState] = useState(null);
  const [rotateState, setRotateState] = useState(null);

  const activeProfileId = activeProfile?.id || null;

  const loadRuntime = useCallback(async () => {
    if (!tenantId) {
      return;
    }

    setIsLoading(true);
    setPanelError("");
    try {
      const [optionsResponse, activeResponse, profilesResponse] = await Promise.all([
        getPlatformTenantLlmRuntimeOptions(tenantId),
        getPlatformTenantLlmRuntimeActive(tenantId),
        getPlatformTenantLlmRuntimeProfiles(tenantId),
      ]);
      const nextProviderOptions = normalizeSaasProviderOptions(optionsResponse?.data || {});
      const nextActiveProfile =
        activeResponse?.data?.profile ||
        activeResponse?.data?.active_profile ||
        activeResponse?.data?.runtime ||
        null;
      const nextProfiles = profilesResponse?.data?.profiles || profilesResponse?.data?.items || [];
      const nextDefaultProvider = getDefaultSaasProvider(nextProviderOptions);

      setProviderOptions(nextProviderOptions);
      setActiveProfile(nextActiveProfile);
      setProfiles(nextProfiles);
      setDraft(createEmptySaasRuntimeDraft(nextDefaultProvider));
      setCredential(createEmptySaasCredential(nextDefaultProvider));
      setFieldErrors({});
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to load tenant LLM runtime.");
      setPanelError(message);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadRuntime();
  }, [loadRuntime]);

  const providerChoices = useMemo(
    () => (providerOptions.length > 0 ? providerOptions : normalizeSaasProviderOptions({})),
    [providerOptions]
  );

  const handleDraftChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    if (field === "provider") {
      setCredential((current) => ({ ...current, provider: value }));
    }
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const handleCredentialChange = (field, value) => {
    setCredential((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const nextErrors = { ...current };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const handleCreateDraft = async (event) => {
    event.preventDefault();
    const errors = {
      ...validateSaasRuntimeDraft(draft),
      ...validateSaasCredential(credential),
    };
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setPanelError("Please fix the highlighted LLM fields before saving.");
      return;
    }

    setActionInFlight("create");
    setPanelError("");
    try {
      await createPlatformTenantLlmRuntimeDraft(
        tenantId,
        buildSaasDraftPayload(draft, credential, { requireApiKey: true })
      );
      toast.success("Tenant LLM runtime draft saved.");
      await loadRuntime();
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to save tenant LLM runtime draft.");
      setPanelError(message);
      toast.error(message);
    } finally {
      setActionInFlight("");
    }
  };

  const openEditDialog = (profile) => {
    setEditState({
      profile,
      draft: profileToSaasDraft(profile),
      credential: profileToSaasCredential(profile),
      errors: {},
      isSaving: false,
    });
  };

  const openRotateDialog = (profile) => {
    setRotateState({
      profile,
      credential: profileToSaasCredential(profile),
      errors: {},
      isSaving: false,
    });
  };

  const updateEditDraft = (field, value) => {
    setEditState((current) => ({
      ...current,
      draft: { ...current.draft, [field]: value },
      credential:
        field === "provider"
          ? { ...current.credential, provider: value }
          : current.credential,
      errors: { ...current.errors, [field]: undefined },
    }));
  };

  const updateEditCredential = (field, value) => {
    setEditState((current) => ({
      ...current,
      credential: { ...current.credential, [field]: value },
      errors: { ...current.errors, [field]: undefined },
    }));
  };

  const updateRotateCredential = (field, value) => {
    setRotateState((current) => ({
      ...current,
      credential: { ...current.credential, [field]: value },
      errors: { ...current.errors, [field]: undefined },
    }));
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    const errors = validateSaasRuntimeDraft(editState.draft);
    if (Object.keys(errors).length > 0) {
      setEditState((current) => ({ ...current, errors }));
      return;
    }

    setEditState((current) => ({ ...current, isSaving: true, errors: {} }));
    try {
      await patchPlatformTenantLlmRuntimeDraft(
        tenantId,
        editState.profile.id,
        buildSaasDraftPayload(editState.draft, editState.credential)
      );
      toast.success("Tenant LLM runtime draft updated.");
      setEditState(null);
      await loadRuntime();
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to update tenant LLM runtime draft.");
      setEditState((current) => ({
        ...current,
        errors: { form: message },
        isSaving: false,
      }));
    }
  };

  const handleRotateCredential = async (event) => {
    event.preventDefault();
    const errors = validateSaasCredential(rotateState.credential);
    if (Object.keys(errors).length > 0) {
      setRotateState((current) => ({ ...current, errors }));
      return;
    }

    setRotateState((current) => ({ ...current, isSaving: true, errors: {} }));
    try {
      await replacePlatformTenantLlmRuntimeCredential(
        tenantId,
        buildSaasCredentialPayload(rotateState.credential)
      );
      toast.success("Tenant LLM credential rotated.");
      setRotateState(null);
      await loadRuntime();
    } catch (error) {
      const message = getBackendErrorMessage(error, "Failed to rotate tenant LLM credential.");
      setRotateState((current) => ({
        ...current,
        errors: { form: message },
        isSaving: false,
      }));
    }
  };

  const runProfileAction = async ({ actionKey, request, successMessage, confirmMessage }) => {
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }
    setActionInFlight(actionKey);
    setPanelError("");
    try {
      await request();
      toast.success(successMessage);
      await loadRuntime();
    } catch (error) {
      const message = getBackendErrorMessage(error, "Tenant LLM runtime action failed.");
      setPanelError(message);
      toast.error(message);
    } finally {
      setActionInFlight("");
    }
  };

  return (
    <div className="platform-drawer-section platform-llm-runtime-panel">
      <div>
        <h3 className="platform-subsection-title">LLM runtime</h3>
        <p className="platform-field-hint">Hosted provider configuration for this tenant.</p>
      </div>

      {isLoading ? (
        <div className="platform-table-state">
          <Loader2 size={16} className="platform-spin" />
          Loading LLM runtime...
        </div>
      ) : null}

      {!isLoading && panelError ? <div className="platform-inline-alert is-error">{panelError}</div> : null}

      {!isLoading ? (
        <>
          {activeProfile ? (
            <div className="platform-inline-alert is-success">
              <div className="platform-inline-alert-copy">
                <strong>{getSaasProviderLabel(activeProfile.provider)} is active</strong>
                <span>{getProfileModelsLabel(activeProfile)}</span>
              </div>
            </div>
          ) : (
            <div className="platform-inline-alert is-warning">
              <div className="platform-inline-alert-copy">
                <strong>No runtime is active</strong>
                <span>Create and activate a draft before tenant users run query jobs.</span>
              </div>
            </div>
          )}

          {!disabled ? (
            <form className="platform-llm-config-card" onSubmit={handleCreateDraft}>
              <div className="platform-form-section-head">
                <h4 className="platform-subsection-title">New runtime config</h4>
                <p>Create a hosted-provider draft with its credential in one save.</p>
              </div>
              <RuntimeFormFields
                draft={draft}
                credential={credential}
                providerOptions={providerChoices}
                errors={fieldErrors}
                onDraftChange={handleDraftChange}
                onCredentialChange={handleCredentialChange}
              />
              <div className="platform-card-actions platform-card-actions--end">
                <Button type="submit" disabled={actionInFlight === "create"}>
                  {actionInFlight === "create" ? <Loader2 size={16} className="platform-spin" /> : <Save size={16} />}
                  {actionInFlight === "create" ? "Saving..." : "Save draft"}
                </Button>
              </div>
            </form>
          ) : null}

          <div className="platform-drawer-section">
            <div className="platform-form-section-head">
              <h4 className="platform-subsection-title">Runtime configs</h4>
              <p>Activate drafts, edit draft settings, rotate the active key, or remove unused configs.</p>
            </div>
            {profiles.length > 0 ? (
              <div className="platform-table-wrap">
                <table className="platform-table platform-llm-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Provider</th>
                      <th>Models</th>
                      <th>Credential</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((profile) => {
                      const status = getSaasProfileStatus(profile, activeProfileId);
                      const isDraft = status === "draft";
                      const isActive = status === "active";
                      return (
                        <tr key={profile.id}>
                          <td><RuntimeProfileBadge status={status} /></td>
                          <td>
                            <strong>{getSaasProviderLabel(profile.provider)}</strong>
                            <div className="platform-customer-email">{getProfileVersionLabel(profile)}</div>
                          </td>
                          <td>{getProfileModelsLabel(profile)}</td>
                          <td>{getProfileCredentialLabel(profile)}</td>
                          <td>
                            <div className="platform-row-actions">
                              {isDraft ? (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(profile)}
                                  >
                                    <Edit3 size={14} />
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={Boolean(actionInFlight)}
                                    onClick={() =>
                                      runProfileAction({
                                        actionKey: `activate-${profile.id}`,
                                        request: () => activatePlatformTenantLlmRuntimeDraft(tenantId, profile.id),
                                        successMessage: "Tenant LLM runtime activated.",
                                      })
                                    }
                                  >
                                    {actionInFlight === `activate-${profile.id}` ? (
                                      <Loader2 size={14} className="platform-spin" />
                                    ) : (
                                      <CheckCircle2 size={14} />
                                    )}
                                    Activate
                                  </Button>
                                </>
                              ) : null}
                              {isActive ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRotateDialog(profile)}
                                >
                                  <KeyRound size={14} />
                                  Rotate key
                                </Button>
                              ) : null}
                              {!isActive ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={Boolean(actionInFlight)}
                                  onClick={() =>
                                    runProfileAction({
                                      actionKey: `delete-${profile.id}`,
                                      request: () =>
                                        isDraft
                                          ? deletePlatformTenantLlmRuntimeDraft(tenantId, profile.id)
                                          : deletePlatformTenantLlmRuntimeProfile(tenantId, profile.id),
                                      successMessage: "Tenant LLM runtime config deleted.",
                                      confirmMessage: "Delete this tenant LLM runtime config?",
                                    })
                                  }
                                >
                                  {actionInFlight === `delete-${profile.id}` ? (
                                    <Loader2 size={14} className="platform-spin" />
                                  ) : (
                                    <Trash2 size={14} />
                                  )}
                                  Delete
                                </Button>
                              ) : (
                                <Button type="button" size="sm" variant="ghost" disabled>
                                  <Eye size={14} />
                                  View only
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="platform-table-state">No runtime configs have been created.</div>
            )}
          </div>
        </>
      ) : null}

      {editState ? (
        <RuntimeDialog title="Edit runtime draft" onClose={() => setEditState(null)}>
          <form className="platform-stack" onSubmit={handleSaveEdit}>
            {editState.errors.form ? (
              <div className="platform-inline-alert is-error">{editState.errors.form}</div>
            ) : null}
            <RuntimeFormFields
              draft={editState.draft}
              credential={editState.credential}
              providerOptions={providerChoices}
              errors={editState.errors}
              onDraftChange={updateEditDraft}
              onCredentialChange={updateEditCredential}
              apiKeyLabel="New API key"
              apiKeyPlaceholder="Leave blank to keep current key"
              requireApiKey={false}
            />
            <div className="platform-card-actions platform-card-actions--end">
              <Button type="button" variant="outline" onClick={() => setEditState(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editState.isSaving}>
                {editState.isSaving ? <Loader2 size={16} className="platform-spin" /> : <Save size={16} />}
                Save draft
              </Button>
            </div>
          </form>
        </RuntimeDialog>
      ) : null}

      {rotateState ? (
        <RuntimeDialog title="Rotate runtime key" onClose={() => setRotateState(null)}>
          <form className="platform-stack" onSubmit={handleRotateCredential}>
            {rotateState.errors.form ? (
              <div className="platform-inline-alert is-error">{rotateState.errors.form}</div>
            ) : null}
            <div className="platform-preview-card">
              <div className="platform-preview-row">
                <span>Provider</span>
                <strong>{getSaasProviderLabel(rotateState.profile.provider)}</strong>
              </div>
              <div className="platform-preview-row">
                <span>Models</span>
                <strong>{getProfileModelsLabel(rotateState.profile)}</strong>
              </div>
            </div>
            <div className="platform-form-grid">
              <label className="platform-field">
                <span>Credential display name</span>
                <Input
                  value={rotateState.credential.displayName}
                  onChange={(event) => updateRotateCredential("displayName", event.target.value)}
                  placeholder={`${getSaasProviderLabel(rotateState.profile.provider)} production key`}
                />
              </label>
              <label className="platform-field">
                <span>New API key <span className="platform-required">*</span></span>
                <Input
                  type="password"
                  value={rotateState.credential.apiKey}
                  onChange={(event) => updateRotateCredential("apiKey", event.target.value)}
                  placeholder="Enter replacement API key"
                  error={Boolean(rotateState.errors.apiKey)}
                />
                {rotateState.errors.apiKey ? (
                  <small className="platform-field-error">{rotateState.errors.apiKey}</small>
                ) : null}
              </label>
            </div>
            <div className="platform-card-actions platform-card-actions--end">
              <Button type="button" variant="outline" onClick={() => setRotateState(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={rotateState.isSaving}>
                {rotateState.isSaving ? <Loader2 size={16} className="platform-spin" /> : <RefreshCw size={16} />}
                Rotate key
              </Button>
            </div>
          </form>
        </RuntimeDialog>
      ) : null}
    </div>
  );
}
